import React, { useState, useCallback } from "react";
import api from "@/api";
import axios from "axios";

const CSVImport = ({ accountId, initialFile, onImportComplete, onClose }) => {
  const [file, setFile] = useState(initialFile || null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(true);

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleFileSelect = (selectedFile) => {
    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
      setImportResult(null); // Clear previous result
    } else {
      alert("Please select a valid CSV file");
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleImport = async () => {
    console.log("🔄 Starting CSV import...");
    if (!file) {
      alert("Please select a file first");
      return;
    }

    try {
      setImporting(true);
      console.log("📤 Sending file to backend...", file.name);

      const formData = new FormData();
      formData.append("file", file);

      // Use axios directly to avoid the default application/json Content-Type
      // Get auth token manually
      const token = localStorage.getItem("auth_token");
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      // Don't set Content-Type - let browser set it with the boundary

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(
        `${BACKEND_URL}/api/accounts/${accountId}/import-csv`,
        formData,
        { headers }
      );

      console.log("✅ Backend response received:", response.data);
      setImportResult(response.data);
      setShowUploadForm(false); // Switch to result view

      // Call parent callback to notify import completed (for future refresh)
      if (onImportComplete) {
        console.log("🔄 Calling onImportComplete callback");
        onImportComplete();
      }
    } catch (error) {
      console.error("❌ Import failed:", error);
      console.error("❌ Error response:", error.response?.data);

      // Extract error message properly
      let errorMessage = "Import failed";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Check if detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err =>
            typeof err === 'string' ? err : err.msg || JSON.stringify(err)
          ).join(', ');
        } else if (typeof detail === 'object') {
          // Single validation error object
          errorMessage = detail.msg || JSON.stringify(detail);
        } else {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      const errorResult = {
        total_rows: 0,
        imported_rows: 0,
        skipped_rows: 0,
        new_holdings_created: 0,
        existing_holdings_updated: 0,
        errors: [errorMessage],
      };
      console.log("❌ Setting error result:", errorResult);
      setImportResult(errorResult);
      setShowUploadForm(false); // Switch to result view even for errors
    } finally {
      setImporting(false);
      console.log("✅ Import process finished");
    }
  };

  const resetToUploadForm = () => {
    setShowUploadForm(true);
    setImportResult(null);
    setFile(null);
  };

  const clearData = async () => {
    console.log("🗑️ Clear data button clicked");
    if (
      window.confirm(
        "This will clear all existing transactions and holdings for this account. Are you sure?"
      )
    ) {
      try {
        console.log("🗑️ Sending delete request to backend...");
        await api.delete(`/accounts/${accountId}/transactions`);
        console.log("✅ Data cleared successfully");
        alert("Account data cleared successfully");
        if (onImportComplete) {
          console.log("🔄 Calling onImportComplete after clear");
          onImportComplete();
        }
        // Close modal after clearing data
        onClose();
      } catch (error) {
        console.error("❌ Error clearing data:", error);
        alert(
          "Error clearing data: " +
            (error.response?.data?.detail || error.message)
        );
      }
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="form-container"
        style={{
          maxWidth: "600px",
          width: "90%",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
        data-testid="csv-import-modal"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <h2>{showUploadForm ? "Import Fidelity CSV" : "Import Results"}</h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
            }}
            data-testid="close-import-button"
          >
            ×
          </button>
        </div>

        {showUploadForm ? (
          <>
            {/* File Upload Area */}
            <div
              className={`file-drop-zone ${dragOver ? "drag-over" : ""}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              style={{
                border: `2px dashed ${dragOver ? "var(--card-hover-border)" : "var(--card-border)"}`,
                borderRadius: "12px",
                padding: "3rem 2rem",
                textAlign: "center",
                background: dragOver ? "var(--add-account-hover-bg)" : "var(--account-type-bg)",
                marginBottom: "1.5rem",
                cursor: file ? "default" : "pointer",
                transition: "all 0.3s ease",
              }}
              onClick={() => !file && document.getElementById("csv-file-input").click()}
              data-testid="file-drop-zone"
            >
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                style={{ display: "none" }}
                data-testid="file-input"
              />

              {file ? (
                <div>
                  <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>
                    📊
                  </div>
                  <div style={{ fontWeight: "600", color: "var(--title-color)" }}>
                    {file.name}
                  </div>
                  <div className="text-muted" style={{ marginTop: "0.5rem" }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>
                    📁
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                      color: "var(--title-color)",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Drop your Fidelity CSV here
                  </div>
                  <div className="text-muted">
                    or click to browse for Activity Report CSV
                  </div>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div
              style={{
                background: "var(--account-type-bg)",
                border: "1px solid var(--card-border)",
                borderRadius: "8px",
                padding: "1rem",
                marginBottom: "1.5rem",
              }}
            >
              <h4 style={{ color: "var(--card-hover-border)", marginBottom: "0.5rem" }}>
                💡 Instructions:
              </h4>
              <ul
                style={{
                  color: "var(--subtitle-color)",
                  fontSize: "0.875rem",
                  margin: 0,
                  paddingLeft: "1.25rem",
                }}
              >
                <li>Download "Activity Report" CSV from Fidelity</li>
                <li>
                  First import will build your complete account from transaction
                  history
                </li>
                <li>
                  Future imports will only add new transactions (deduplication
                  included)
                </li>
                <li>Existing holdings will be recalculated automatically</li>
              </ul>
            </div>

            {/* Remove old import result section - moved below */}

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "space-between",
              }}
            >
              <button
                className="btn btn-danger"
                onClick={clearData}
                style={{ fontSize: "0.875rem" }}
                data-testid="clear-data-button"
              >
                🗑️ Clear All Data
              </button>

              <div style={{ display: "flex", gap: "1rem" }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={importing}
                  data-testid="cancel-import-button"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={!file || importing}
                  data-testid="import-csv-button"
                >
                  {importing ? "Importing..." : "📊 Import CSV"}
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Results View */
          <div style={{ padding: "1rem" }}>
            <div
                style={{
                  background:
                    importResult?.errors && importResult.errors.length > 0
                      ? "var(--performance-negative-bg)"
                      : "var(--performance-positive-bg)",
                border: `1px solid ${
                  importResult?.errors && importResult.errors.length > 0
                    ? "var(--color-negative)"
                    : "var(--color-positive)"
                }`,
                borderRadius: "8px",
                padding: "1.5rem",
                marginBottom: "1.5rem",
              }}
              data-testid="import-result"
            >
              <h3
                style={{
                  color:
                    importResult?.errors && importResult.errors.length > 0
                      ? "var(--performance-negative-text)"
                      : "var(--performance-positive-text)",
                  marginBottom: "1rem",
                  textAlign: "center",
                }}
              >
                {importResult?.errors && importResult.errors.length > 0
                  ? "⚠️ Import Complete with Errors"
                  : "✅ Import Successful"}
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    padding: "0.75rem",
                    background: "var(--account-type-bg)",
                    borderRadius: "6px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color: "var(--title-color)",
                    }}
                  >
                    {importResult?.total_rows || 0}
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                    Transactions in File
                  </div>
                </div>

                <div
                  style={{
                    textAlign: "center",
                    padding: "0.75rem",
                    background: "var(--account-type-bg)",
                    borderRadius: "6px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color: importResult?.imported_rows > 0 ? "var(--color-positive)" : "var(--subtitle-color)",
                    }}
                  >
                    {importResult?.imported_rows || 0}
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                    New Transactions
                  </div>
                </div>

                <div
                  style={{
                    textAlign: "center",
                    padding: "0.75rem",
                    background: "var(--account-type-bg)",
                    borderRadius: "6px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color: "var(--title-color)",
                    }}
                  >
                    {importResult?.existing_holdings_updated || 0}
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                    Holdings Updated
                  </div>
                </div>
              </div>

              {importResult?.errors && importResult.errors.length > 0 && (
                <div
                  style={{
                    background: "var(--account-type-bg)",
                    border: "1px solid var(--card-border)",
                    borderRadius: "6px",
                    padding: "1rem",
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  <div
                    style={{
                      color: "var(--color-negative)",
                      fontWeight: "600",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Errors ({importResult.errors.length}):
                  </div>
                  {importResult.errors.map((error, index) => (
                    <div
                      key={index}
                      style={{
                        color: "var(--color-negative)",
                        fontSize: "0.875rem",
                        marginBottom: "0.25rem",
                      }}
                    >
                      • {error}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div
              style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
            >
              <button
                className="btn btn-secondary"
                onClick={resetToUploadForm}
                data-testid="import-another-button"
              >
                📊 Import Another File
              </button>
              <button
                className="btn btn-primary"
                onClick={onClose}
                data-testid="close-results-button"
              >
                ✅ Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVImport;
